/**
 * Yeni resmi tatil ekleme modal'ı.
 */

import { DatePicker } from '@/components/ui/DatePicker'
import { useEffect, useState } from 'react'
import { Save } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/contexts/AuthContext'
import {
  tatilEkle, tatilGuncelle,
  KATSAYI_ETIKETLERI,
  type ResmiTatil, type TatilKatsayisi,
} from '@/lib/services/tatil-service'
import type { IsoTarih } from '@/types'

interface Props {
  acik: boolean
  duzenlenen?: ResmiTatil | null  // null/undefined = yeni
  onKapat: () => void
  onKaydedildi: () => void
}

const KATSAYI_DEGERLERI: TatilKatsayisi[] = ['calismaz', 1, 1.5, 2]

export function TatilEkleModal({ acik, duzenlenen, onKapat, onKaydedildi }: Props) {
  const { firebaseUser } = useAuth()
  const [tarih, setTarih] = useState('')
  const [isim, setIsim] = useState('')
  const [katsayi, setKatsayi] = useState<TatilKatsayisi>(1.5)
  const [dini, setDini] = useState(false)
  const [kaydediliyor, setKaydediliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    if (!acik) return
    if (duzenlenen) {
      setTarih(duzenlenen.tarih)
      setIsim(duzenlenen.isim)
      setKatsayi(duzenlenen.katsayi)
      setDini(duzenlenen.dini)
    } else {
      setTarih('')
      setIsim('')
      setKatsayi(1.5)
      setDini(false)
    }
    setHata(null)
  }, [acik, duzenlenen])

  async function kaydet() {
    if (!firebaseUser) return
    if (!tarih || !isim.trim()) {
      setHata('Tarih ve isim zorunlu')
      return
    }
    setKaydediliyor(true)
    setHata(null)
    try {
      if (duzenlenen) {
        await tatilGuncelle(duzenlenen.id, { isim, katsayi, dini })
      } else {
        await tatilEkle(
          { tarih: tarih as IsoTarih, isim, katsayi, dini },
          firebaseUser.uid
        )
      }
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  return (
    <Modal
      acik={acik}
      onKapat={onKapat}
      baslik={duzenlenen ? 'Tatili Düzenle' : 'Yeni Resmi Tatil'}
      boyut="md"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Tarih <span className="text-red-500">*</span>
          </label>
          <DatePicker
  value={tarih}
  onChange={setTarih}
  disabled={!!duzenlenen}
/>
          {duzenlenen && (
            <p className="text-xs text-gray-500 mt-1">
              Tarih değiştirilemez. Silip yeniden ekle.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Tatil Adı <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={isim}
            onChange={(e) => setIsim(e.target.value)}
            maxLength={80}
            placeholder="Örn: Cumhuriyet Bayramı"
            className="input-base"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Katsayı
          </label>
          <div className="grid grid-cols-2 gap-2">
            {KATSAYI_DEGERLERI.map((k) => (
              <label
                key={String(k)}
                className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                  katsayi === k
                    ? 'bg-brand-50 border-brand-500 shadow-sm'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="katsayi"
                  checked={katsayi === k}
                  onChange={() => setKatsayi(k)}
                />
                <span className="text-sm font-semibold">
                  {KATSAYI_ETIKETLERI[String(k)]}
                </span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            <strong>Çalışmaz:</strong> Ofis personeli için izinli. Market personeli çalışırsa x2 sayılır.<br />
            <strong>x1:</strong> Normal ücret. <strong>x1.5/x2:</strong> Bayram zammı.
          </p>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={dini}
            onChange={(e) => setDini(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm text-gray-700">
            Dini bayram (her yıl tarihi değişir, yıl başında yenilenmeli)
          </span>
        </label>

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onKapat}
            disabled={kaydediliyor}
            className="btn-secondary flex-1"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={kaydet}
            disabled={kaydediliyor}
            className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" />
            {kaydediliyor ? 'Kaydediliyor...' : (duzenlenen ? 'Güncelle' : 'Ekle')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
